import React from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Checkbox } from '@/components/ui/checkbox';
import { Button } from '@/components/ui/button';
import { DatePickerInput } from '@/components/ui/date-picker';
import { LegacySchedule } from '@/types/display';

interface BasicScheduleTabProps {
  schedule: LegacySchedule | null;
  onChange: (schedule: LegacySchedule) => void;
}

const DAYS_OF_WEEK = [
  'Monday',
  'Tuesday',
  'Wednesday',
  'Thursday',
  'Friday',
  'Saturday',
  'Sunday'
];

const SEASON_PRESETS = [
  {
    name: 'Christmas Season',
    start_date: `${new Date().getFullYear()}-11-24`,
    end_date: `${new Date().getFullYear() + 1}-01-06`,
  },
  {
    name: 'Halloween Season',
    start_date: `${new Date().getFullYear()}-10-01`,
    end_date: `${new Date().getFullYear()}-10-31`,
  },
  {
    name: '4th of July',
    start_date: `${new Date().getFullYear()}-07-01`,
    end_date: `${new Date().getFullYear()}-07-07`,
  },
];

const BasicScheduleTab: React.FC<BasicScheduleTabProps> = ({
  schedule,
  onChange,
}) => {
  const currentSchedule: LegacySchedule = schedule || {
    start_date: '',
    end_date: '',
    days: [],
    hours: {
      start: '18:00',
      end: '22:00',
    },
  };

  const updateSchedule = (updates: Partial<LegacySchedule>) => {
    onChange({
      ...currentSchedule,
      ...updates,
    });
  };

  const handleDayToggle = (day: string, checked: boolean) => {
    const newDays = checked
      ? [...currentSchedule.days, day]
      : currentSchedule.days.filter(d => d !== day);
    updateSchedule({ days: newDays });
  };

  const applyPreset = (preset: typeof SEASON_PRESETS[0]) => {
    updateSchedule({
      start_date: preset.start_date,
      end_date: preset.end_date,
    });
  };

  const selectAllDays = () => {
    updateSchedule({ days: [...DAYS_OF_WEEK] });
  };

  const selectWeekdays = () => {
    updateSchedule({
      days: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday']
    });
  };

  const selectWeekends = () => {
    updateSchedule({
      days: ['Saturday', 'Sunday']
    });
  };

  const clearAllDays = () => {
    updateSchedule({ days: [] });
  };

  return (
    <div className="space-y-6">
      {/* Season Dates */}
      <div>
        <Label className="text-base font-medium mb-3 block">Season Dates</Label>
        <div className="grid grid-cols-2 gap-4 mb-3">
          <div>
            <Label htmlFor="start_date" className="text-sm">Start Date</Label>
            <DatePickerInput
              value={currentSchedule.start_date}
              onChange={(value) => updateSchedule({ start_date: value })}
              placeholder="Select start date"
            />
          </div>
          <div>
            <Label htmlFor="end_date" className="text-sm">End Date</Label>
            <DatePickerInput
              value={currentSchedule.end_date}
              onChange={(value) => updateSchedule({ end_date: value })}
              placeholder="Select end date"
            />
          </div>
        </div>

        {/* Quick Presets */}
        <div className="flex flex-wrap gap-2">
          {SEASON_PRESETS.map((preset) => (
            <Button
              key={preset.name}
              type="button"
              variant="outline"
              size="sm"
              onClick={() => applyPreset(preset)}
              className="text-xs"
            >
              {preset.name}
            </Button>
          ))}
        </div>
      </div>

      {/* Days of Operation */}
      <div>
        <div className="flex items-center justify-between mb-3">
          <Label className="text-base font-medium">Days of Operation</Label>
          <div className="flex gap-2">
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={selectAllDays}
              className="text-xs"
            >
              All Days
            </Button>
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={selectWeekdays}
              className="text-xs"
            >
              Weekdays
            </Button>
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={selectWeekends}
              className="text-xs"
            >
              Weekends
            </Button>
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={clearAllDays}
              className="text-xs text-red-600 hover:text-red-700"
            >
              Clear
            </Button>
          </div>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {DAYS_OF_WEEK.map((day) => (
            <div key={day} className="flex items-center space-x-2">
              <Checkbox
                id={`day-${day}`}
                checked={currentSchedule.days.includes(day)}
                onCheckedChange={(checked) => handleDayToggle(day, checked as boolean)}
              />
              <Label
                htmlFor={`day-${day}`}
                className="text-sm font-normal cursor-pointer"
              >
                {day}
              </Label>
            </div>
          ))}
        </div>
      </div>

      {/* Daily Hours */}
      <div>
        <Label className="text-base font-medium mb-3 block">Daily Hours</Label>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <Label htmlFor="start_time" className="text-sm">Start Time</Label>
            <Input
              type="time"
              id="start_time"
              value={currentSchedule.hours.start}
              onChange={(e) => updateSchedule({
                hours: {
                  ...currentSchedule.hours,
                  start: e.target.value
                }
              })}
            />
          </div>
          <div>
            <Label htmlFor="end_time" className="text-sm">End Time</Label>
            <Input
              type="time"
              id="end_time"
              value={currentSchedule.hours.end}
              onChange={(e) => updateSchedule({
                hours: {
                  ...currentSchedule.hours,
                  end: e.target.value
                }
              })}
            />
          </div>
        </div>
        <p className="text-sm text-muted-foreground mt-2">
          These hours will apply to all selected days
        </p>
      </div>
    </div>
  );
};

export default BasicScheduleTab;
